import { useState } from 'react';
import { useAuth } from '../lib/AuthContext';
import { Lock, Sparkles, Check, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import api from '../lib/api';
import { toast } from 'sonner';

// Feature metadata for UI display (kept for display purposes)
const FEATURE_METADATA = {
  dayView: { name: 'Day View', description: 'View and manage daily schedules', minTier: 'light' },
  weekView: { name: 'Week View', description: 'View and manage weekly schedules', minTier: 'light' },
  basicReports: { name: 'Basic Reports', description: 'Simple scheduling reports', minTier: 'light' },
  advancedReports: { name: 'Advanced Reports', description: 'Detailed analytics and insights', minTier: 'standard' },
  wageCostCalculations: { name: 'Wage Cost Calculations', description: 'Real-time cost tracking', minTier: 'standard' },
  breakManagement: { name: 'Break Management', description: 'Configure and track breaks', minTier: 'standard' },
  overtimeRates: { name: 'Overtime & Custom Rates', description: 'Multiple pay rates per employee', minTier: 'standard' },
  absenceTracking: { name: 'Absence & Leave Tracking', description: 'Holiday and sick leave management', minTier: 'standard' },
  clockInOut: { name: 'Clock In/Out', description: 'Time tracking for employees', minTier: 'professional', comingSoon: true },
  payrollIntegrations: { name: 'Payroll Integrations', description: 'Export to Sage, ADP, and more', minTier: 'professional', comingSoon: true },
  multipleLocations: { name: 'Multiple Locations', description: 'Manage staff across multiple sites', minTier: 'professional', comingSoon: true },
  multipleDepartments: { name: 'Multiple Departments', description: 'Organize staff by department', minTier: 'professional', comingSoon: true },
  csvExport: { name: 'CSV Export', description: 'Export data to CSV', minTier: 'standard' },
  pdfExport: { name: 'PDF Export', description: 'Export reports to PDF', minTier: 'standard' }
};

// Check if feature is coming soon
const isFeatureComingSoon = (feature) => {
  return FEATURE_METADATA[feature]?.comingSoon === true;
};

// Upgrade Modal Component
const UpgradeModal = ({ open, onClose, feature, currentTier, requiredTier }) => {
  const [loading, setLoading] = useState(false);
  const [tierInfo, setTierInfo] = useState(null);
  const { subscription } = useAuth();
  const featureMeta = FEATURE_METADATA[feature];
  const isComingSoon = isFeatureComingSoon(feature);

  // Get tier info from subscription's available tiers
  const requiredTierConfig = subscription?.available_tiers?.[requiredTier] || {
    name: requiredTier?.charAt(0).toUpperCase() + requiredTier?.slice(1) || 'Standard',
    price: 29,
    feature_flags: {}
  };

  // Map tier to package ID
  const getPackageId = (tier) => {
    const packageMap = {
      'light': 'light_monthly',
      'standard': 'standard_monthly',
      'professional': 'professional_monthly'
    };
    return packageMap[tier] || 'standard_monthly';
  };

  const handleUpgrade = async () => {
    setLoading(true);
    try {
      const packageId = getPackageId(requiredTier);
      const response = await api.post('/checkout/create-session', {
        package_id: packageId,
        origin_url: window.location.origin
      });
      
      if (response.data.checkout_url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.checkout_url;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Failed to start checkout process');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          {isComingSoon ? (
            <>
              <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-6 h-6 text-amber-600 dark:text-amber-400" />
              </div>
              <DialogTitle className="text-center">Coming Soon</DialogTitle>
              <DialogDescription className="text-center">
                <span className="font-medium text-foreground">{featureMeta?.name}</span> is currently in development and will be available soon on the Professional plan.
              </DialogDescription>
            </>
          ) : (
            <>
              <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mx-auto mb-4">
                <Lock className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <DialogTitle className="text-center">Upgrade to {requiredTierConfig?.name}</DialogTitle>
              <DialogDescription className="text-center">
                <span className="font-medium text-foreground">{featureMeta?.name}</span> requires the {requiredTierConfig?.name} plan or higher.
              </DialogDescription>
            </>
          )}
        </DialogHeader>

        {!isComingSoon && (
          <div className="space-y-4 py-4">
            {/* Plan comparison */}
            <div className="bg-muted/50 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="font-semibold">{requiredTierConfig?.name} Plan</span>
                <span className="text-2xl font-bold">£{requiredTierConfig?.price}<span className="text-sm font-normal text-muted-foreground">/mo</span></span>
              </div>
              <ul className="space-y-2">
                {requiredTierConfig?.feature_flags && Object.entries(requiredTierConfig.feature_flags)
                  .filter(([_, v]) => v === true)
                  .slice(0, 6)
                  .map(([feat]) => (
                    <li key={feat} className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      <span className="text-muted-foreground">
                        {FEATURE_METADATA[feat]?.name || feat.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                      </span>
                    </li>
                  ))}
                {requiredTierConfig?.feature_flags && 
                  Object.entries(requiredTierConfig.feature_flags).filter(([_, v]) => v === true).length > 6 && (
                  <li className="text-sm text-muted-foreground pl-6">
                    +{Object.entries(requiredTierConfig.feature_flags).filter(([_, v]) => v === true).length - 6} more features
                  </li>
                )}
              </ul>
            </div>

            <Button 
              className="w-full bg-blue-600 hover:bg-blue-700" 
              size="lg"
              onClick={handleUpgrade}
              disabled={loading}
              data-testid="upgrade-now-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  Upgrade Now
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
            
            <p className="text-center text-xs text-muted-foreground">
              30-day money-back guarantee • Cancel anytime
            </p>
          </div>
        )}

        {isComingSoon && (
          <div className="py-4">
            <Button variant="outline" className="w-full" onClick={() => onClose(false)}>
              Got it
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Component to gate features - shows grayed out with click to upgrade
export const FeatureGate = ({ 
  feature, 
  children,
  className = ''
}) => {
  const { company, subscription, hasFeatureAccess } = useAuth();
  const [showUpgrade, setShowUpgrade] = useState(false);
  
  const tier = subscription?.subscription_tier || 
               (company?.subscription_status === 'trial' ? 'trial' : 'light');
  
  const hasAccess = hasFeatureAccess(feature);
  const comingSoon = isFeatureComingSoon(feature);
  const featureMeta = FEATURE_METADATA[feature];

  // If user has access and it's not coming soon, render normally
  if (hasAccess && !comingSoon) {
    return children;
  }

  // Render grayed out with click handler
  return (
    <>
      <div 
        className={`relative cursor-pointer group ${className}`}
        onClick={() => setShowUpgrade(true)}
      >
        {/* Grayed out content */}
        <div className="opacity-40 pointer-events-none select-none">
          {children}
        </div>
        
        {/* Overlay on hover */}
        <div className="absolute inset-0 bg-background/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-lg">
          <div className="bg-background border shadow-lg rounded-lg px-4 py-2 flex items-center gap-2">
            {comingSoon ? (
              <>
                <Sparkles className="w-4 h-4 text-amber-500" />
                <span className="text-sm font-medium">Coming Soon</span>
              </>
            ) : (
              <>
                <Lock className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium">Upgrade to unlock</span>
              </>
            )}
          </div>
        </div>
      </div>

      <UpgradeModal 
        open={showUpgrade}
        onClose={setShowUpgrade}
        feature={feature}
        currentTier={tier}
        requiredTier={featureMeta?.minTier || 'standard'}
      />
    </>
  );
};

// Simpler inline gate for buttons/links
export const FeatureButton = ({ 
  feature, 
  children, 
  onClick,
  ...props 
}) => {
  const { company, subscription, hasFeatureAccess } = useAuth();
  const [showUpgrade, setShowUpgrade] = useState(false);
  
  const tier = subscription?.subscription_tier || 
               (company?.subscription_status === 'trial' ? 'trial' : 'light');
  
  const hasAccess = hasFeatureAccess(feature);
  const comingSoon = isFeatureComingSoon(feature);
  const featureMeta = FEATURE_METADATA[feature];

  const handleClick = (e) => {
    if (!hasAccess || comingSoon) {
      e.preventDefault();
      setShowUpgrade(true);
    } else if (onClick) {
      onClick(e);
    }
  };

  return (
    <>
      <Button 
        {...props} 
        onClick={handleClick}
        className={`${props.className || ''} ${(!hasAccess || comingSoon) ? 'opacity-60' : ''}`}
      >
        {children}
        {(!hasAccess || comingSoon) && (
          <Lock className="w-3 h-3 ml-2" />
        )}
      </Button>

      <UpgradeModal 
        open={showUpgrade}
        onClose={setShowUpgrade}
        feature={feature}
        currentTier={tier}
        requiredTier={featureMeta?.minTier || 'standard'}
      />
    </>
  );
};

// Hook to check feature access
export const useFeatureAccess = (feature) => {
  const { company, subscription, hasFeatureAccess } = useAuth();
  const tier = subscription?.subscription_tier || 
               (company?.subscription_status === 'trial' ? 'trial' : 'light');
  
  return {
    hasAccess: hasFeatureAccess(feature),
    isComingSoon: isFeatureComingSoon(feature),
    currentTier: tier,
    requiredTier: FEATURE_METADATA[feature]?.minTier
  };
};

// Nav item with lock indicator
export const FeatureNavItem = ({ 
  feature, 
  children, 
  to,
  icon: Icon,
  className = ''
}) => {
  const { company, subscription, hasFeatureAccess } = useAuth();
  const [showUpgrade, setShowUpgrade] = useState(false);
  
  const tier = subscription?.subscription_tier || 
               (company?.subscription_status === 'trial' ? 'trial' : 'light');
  
  const hasAccess = hasFeatureAccess(feature);
  const comingSoon = isFeatureComingSoon(feature);
  const featureMeta = FEATURE_METADATA[feature];

  if (hasAccess && !comingSoon) {
    return children;
  }

  return (
    <>
      <button
        onClick={() => setShowUpgrade(true)}
        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-all duration-200 opacity-60 ${className}`}
      >
        {Icon && <Icon className="w-4 h-4" />}
        <span className="hidden sm:inline">{typeof children === 'string' ? children : 'Feature'}</span>
        <Lock className="w-3 h-3 ml-1" />
      </button>

      <UpgradeModal 
        open={showUpgrade}
        onClose={setShowUpgrade}
        feature={feature}
        currentTier={tier}
        requiredTier={featureMeta?.minTier || 'standard'}
      />
    </>
  );
};

export default FeatureGate;
